import { BehaviorSubject, Observable, of, map, catchError, shareReplay } from "rxjs";
import { AuthService } from "./auth.service";
import { Injectable } from "@angular/core";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';
import * as FileSaver from 'file-saver';
import { MessageManagerService } from "./messagemanagerService.service";
import { APIService } from "./api.service";
import { ConfigurationService } from "./configuration.service";
import { TranslateService } from "@ngx-translate/core";

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

export interface PaginationConfiguration {
    defaultPageSize: number;
    pageOptions: number[];
    maxPageSize: number;
}

@Injectable({
    providedIn: 'root'
})
export class CommonService {
    reloadValidationTime = 5000;
    private valueSource = new BehaviorSubject<boolean>(false);
    currentValue = this.valueSource.asObservable();
    selectedWalletIdForReset: any;
    
    // Centralized pagination configuration with caching
    // Initialize with default values to prevent null errors
    private paginationConfigSubject = new BehaviorSubject<number[]>([10, 25, 50, 100]);
    public paginationOptions$: Observable<number[]>;
    private paginationConfigLoaded = false;
    

    constructor(
        private authService: AuthService,
        private messageService: MessageManagerService,
        private api: APIService,
        private configurationService: ConfigurationService,
        private translate: TranslateService
    ) { 
        // Use shareReplay to cache the result and share it across all subscribers
        // This ensures the data is loaded only once even with multiple subscriptions
        this.paginationOptions$ = this.paginationConfigSubject.asObservable().pipe(
            shareReplay({ bufferSize: 1, refCount: false })
        );
        
        this.initializePaginationConfiguration();
    }
    public hasPrivilege(privilege: any) {
        return this.authService.hasPrivilege(privilege);
    }

    setValue(value: boolean) {
        this.valueSource.next(value);
    }

    getValue(): boolean {
        return this.valueSource.value;
    }

    setResetId(value: any) {
        this.selectedWalletIdForReset = value;
    }

    getResetId() {
        return this.selectedWalletIdForReset;
    }

    downloadPDF(fileName: any, data: any) {
        const doc = new jsPDF();
        const content = this.formatSendMoneyContent(data);
        doc.text(content, 10, 10);
        doc.save(`${fileName}.pdf`);
    }

    convertUtcToLocal(utcDateString: string): string {
        const trimmedDateString = utcDateString.split('.')[0] + 'Z';

        const utcDate = new Date(trimmedDateString);
        return utcDate.toLocaleString(); // Convert to local time based on browser settings
    }

    formatSendMoneyContent(data: any): string {
        let content = '';

        // Check if batchSubscribersData exists and is not empty
        if (data && data.length > 0) {
            data.forEach((item: { [x: string]: any; hasOwnProperty: (arg0: string) => any; }, index: number) => {
                content += `Record ${index + 1}:\n`;
                for (const key in item) {
                    if (item.hasOwnProperty(key)) {
                        content += `  ${key}: ${item[key]}\n`;
                    }
                }
                content += '\n'; // Add a newline between items
            });
            return content;
        }

        else {
            return 'No content available';
        }
    }

    downloadAsExcel(fileName: any, data: any) {
        const wb: XLSX.WorkBook = XLSX.utils.book_new();

        if (data && data.length > 0) {
            const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
            XLSX.utils.book_append_sheet(wb, ws, fileName);
        }

        if ((!data || data.length === 0)) {
            const noContent = [{ 'Message': 'No content available' }];
            const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(noContent);
            XLSX.utils.book_append_sheet(wb, ws, 'No Data');
        }

        const wbout: ArrayBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `${fileName}.xlsx`);
    }
    // Utility function to convert text to Title Case
    toTitleCase(str: any) {
        // Updated regex to cover "Waiting for Approval(L1)" and "Waiting for Approval(L 1)"
        const approvalRegex = /^Waiting for Approval\s*\(L[1-6]\)$/;
        const approvalRegex1 = /^Waiting for Approval\s*\(L [1-6]\)$/;

        if (str === null || approvalRegex.test(str) || approvalRegex1.test(str)) {
            return str; // No changes if it matches the regex
        } else {
            return str
                .toLowerCase()
                .split(' ')
                .map((word: string) => {
                    return word.charAt(0).toUpperCase() + word.slice(1);
                })
                .join(' ');
        }
    }



    formatCurrency(amount: string | number): string {
        if (!amount || isNaN(Number(amount)) || amount == null) {
            return 'XOF 0';
        }
        return `XOF ${Number(amount).toLocaleString('en-US')}`;
    }
    formatCurrencyTOGO(amount: string | number): string {
        if (!amount || isNaN(Number(amount)) || amount == null) {
            return 'XOF 0';
        }
        return `XOF ${Number(amount).toLocaleString('en-US')}`;
    }

    // we are using these functions through out portal for export data in tabular format
    exportToExcel(columns: any[], data: any[], useCaseName: string) {
        if (data.length > 0) {
            const selectedColumns = columns.filter(
                col => col.header !== 'Select' && col.header !== 'Action');
            const exportData = data.map((row, index) => {
                const exportRow: any = { 'S.No': index + 1 };
                selectedColumns.forEach(col => {
                    const value = row[col.field];
                    exportRow[col.header] = typeof value === 'string' ? this.stripHtmlTags(value) : value;
                });
                return exportRow;
            });
            const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
            const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
            const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            this.saveAsExcelFile(excelBuffer, `${useCaseName}`);
        } else {
            this.messageService.add({
                severity: 'info',
                summary: this.translate.instant('COMMON.INFO'),
                detail: this.translate.instant('COMMON.NO_CONTENT_AVAILABLE'),
                life: 3000
            });
        }
    }


    saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
        const currentDate = new Date();
        const formattedDate = `${currentDate.getDate()}/${currentDate.getMonth() + 1}/${currentDate.getFullYear()}`; // Format as DD/MM/YYYY
        FileSaver.saveAs(data, fileName + '_' + formattedDate + EXCEL_EXTENSION);


    }
    exportToPDF(columns: any[], data: any[], useCaseName: string, title: string = 'Transaction History', pageSize: string = 'a4') {
        if (data.length > 0) {
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: pageSize
            });
            doc.text(title, 14, 16);
            const selectedColumns = columns.filter(
                col => col.header !== 'Select' && col.header !== 'Action');
            // Check if "S.No" already exists in selectedColumns
            const hasSerialNumber = selectedColumns.some(
                col => col.field === 'serialNumber' && col.header === 'S.No'
            );

            // Only add 'S.No' to header if it's not already in the selected columns
            const header = hasSerialNumber
                ? selectedColumns.map(col => col.header)
                : ['S.No', ...selectedColumns.map(col => col.header)];

            const body = data.map((row, index) => {
                const rowData = selectedColumns.map(col => {
                    const value = row[col.field];
                    return typeof value === 'string' ? this.stripHtmlTags(value) : value;
                });
                return hasSerialNumber ? rowData : [index + 1, ...rowData];
            });


            (doc as any).autoTable({
                head: [header],
                body: body,
                startY: 25,
                styles: {
                    fontSize: 10
                },
                headStyles: {
                    fontSize: 10
                }
            });
            const currentDate = new Date();
            const formattedDate = `${currentDate.getDate()}/${currentDate.getMonth() + 1}/${currentDate.getFullYear()}`; // Format as DD/MM/YYYY
            doc.save(`${useCaseName}_${formattedDate}.pdf`);
        } else {
            this.messageService.add({
                severity: 'info',
                summary: this.translate.instant('COMMON.INFO'),
                detail: this.translate.instant('COMMON.NO_CONTENT_AVAILABLE'),
                life: 3000
            });
        }
    }
    toLocalDateString(date: Date): string {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    stripHtmlTags(html: string): string {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    }

    /**
     * Initialize pagination configuration from backend
     * This method is called only once when the service is created
     * The result is cached and shared across all subscribers using shareReplay
     */
    private initializePaginationConfiguration(): void {
        // Prevent multiple calls if already loaded or loading
        if (this.paginationConfigLoaded) {
            return;
        }
        
        this.paginationConfigLoaded = true;
        
        this.configurationService.getPortalConfigurations().subscribe({
            next: (response: any) => {
                const paginationConfig = response?.data?.find((item: any) => 
                    item.useCase === 'PaginationPageSizeOptions'
                );
                
                if (paginationConfig?.stringValue) {
                    const options = paginationConfig.stringValue.split(',')
                        .map((s: string) => parseInt(s.trim(), 10))
                        .filter((n: number) => !isNaN(n) && n > 0)
                        .sort((a: number, b: number) => a - b);
                    
                    this.paginationConfigSubject.next(options);
                } else {
                    // Fallback to default values if no configuration found
                    this.paginationConfigSubject.next([10, 25, 50, 100]);
                }
            },
            error: (error: any) => {
                // Fallback to default values on error
                this.paginationConfigSubject.next([10, 25, 50, 100]);
            }
        });
    }
    
    /**
     * Method to manually reload pagination configuration if needed
     * Can be called from components if refresh is required
     */
    public reloadPaginationConfiguration(): void {
        this.paginationConfigLoaded = false;
        this.initializePaginationConfiguration();
    }

}
